import { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { ResponsiveProps } from 'common/types/reactGridLayout'
import { SetSelectorValueParams } from '../../../common/types/model'
import {
  actionCallback,
  IBaseComponent,
  GetOptionsAndIndexMethod,
  ISelectorComponent,
  GenericComponent,
  IMenu,
  IMinBaseComponent
} from '../components/types'
import { InterfaceTranslation } from '../types'

export enum InterfaceEventType {
  ShowQuickPivot = 'ShowQuickPivotEvent',
  ChoiceSelect = 'ChoiceSelectEvent',
  ColumnResize = 'ColumnResizeEvent',
  CreateComponent = 'CreateComponentEvent',
  ChangeComponent = 'ChangeComponentEvent',
  Drilldown = 'DrilldownEvent',
  Filter = 'FilterEvent',
  Hover = 'HoverEvent',
  InitialUpdate = 'InitialUpdateEvent',
  Paginate = 'PaginateEvent',
  Pivot = 'PivotEvent',
  Relayout = 'RelayoutEvent',
  RemoveComponent = 'RemoveComponentEvent',
  Selected = 'SelectedEvent',
  Select = 'SelectEvent',
  SetInputValue = 'SetInputValueEvent',
  Sort = 'SortEvent',
  TableAddRow = 'TableAddRowEvent',
  TableChange = 'TableChangeEvent',
  TableRemoveRow = 'TableRemoveRowEvent',
  UpdateLayout = 'UpdateLayoutEvent',
  UpdateProperties = 'UpdatePropertiesEvent',
  ButtonEvent = 'ButtonEvent',
  ShowDataHandlingAreaEvent = 'ShowDataHandlingAreaEvent',
  UpdateComponentCode = 'UpdateComponentCodeEvent',
  UpdateChartType = 'UpdateChartType',
  FormAddRowEvent = 'FormAddRowEvent',
  FormRemoveRowEvent = 'FormRemoveRowEvent',
  FormChangeEvent = 'FormChangeEvent',
  FormConfirmChangesEvent = 'FormConfirmChangesEvent',
  FormCancelChangesEvent = 'FormCancelChangesEvent',
  FormRefreshDataEvent = 'FormRefreshDataEvent',
  ApplyHierarchyEvent = 'ApplyHierarchyEvent',
  UpdateCalculatedFieldEvent = 'UpdateCalculatedFieldEvent',
  ResetViewEvent = 'ResetViewEvent',
  SelectAllEvent = 'SelectAllEvent',
  FormChangeRowsToAddEvent = 'FormChangeRowsToAddEvent',
  CompareScenariosEvent = 'CompareScenariosEvent',
  CompareInterfaceScenariosEvent = 'CompareInterfaceScenariosEvent',
  RefreshEvent = 'RefreshEvent',
  UpdateCalculatedItemEvent = 'UpdateCalculatedItemEvent',
  DynamicHtmlEvent = 'DynamicHtmlEvent',
  InputCubeChangeEvent = 'InputCubeChangeEvent',
  InputCubeConfirmChangesEvent = 'InputCubeConfirmChangesEvent',
  InputCubeCancelChangesEvent = 'InputCubeCancelChangesEvent',
  ApplySubtotalEvent = 'ApplySubtotalEvent',
  FilterComponentEvent = 'FilterComponentEvent',
  ApplyTotalEvent = 'ApplyTotalEvent',
  ChangeProcessingEngineEvent = 'ChangeProcessingEngineEvent',
  FilterAllEvent = 'FilterAllEvent',
  AddUploadManagerItemEvent = 'AddUploadManagerItemEvent',
  UpdateUploadManagerItemEvent = 'UpdateUploadManagerItemEvent',
  DeleteUploadManagerItemEvent = 'DeleteUploadManagerItemEvent',
  OrderUploadManagerItemsEvent = 'OrderUploadManagerItemsEvent',
  ClearFiltersEvent = 'ClearFiltersEvent',
  CustomSortEvent = 'CustomSortEvent',
  MultiSortEvent = 'MultiSortEvent',
  UndoRedoComponentCodeEvent = 'UndoRedoComponentCodeEvent',
  UndoRedoDynamicHtmlEvent = 'UndoRedoDynamicHtmlEvent',
  BuildAutomaticDateHierarchiesEvent = 'BuildAutomaticDateHierarchiesEvent',
  UpdateScenarioPlacementEvent = 'UpdateScenarioPlacementEvent',
  ConditionFilter = 'ConditionFilterEvent',
  NodeFilter = 'NodeFilterEvent'
}

export enum Context {
  Code,
  Interfaces
}

export interface InterfaceCustomView {
  id: string
  /** null: shown with a generic label */
  name: string | null
  isDefault: boolean
  updatedAt?: string
}

export type GridLayoutProperties = {
  grid: ResponsiveProps
  name: string
  id?: string
  code?: string
  documentation?: string
  hasDocumentation?: boolean
  icon?: IconDefinition
  hasCustomView?: boolean
  customViewActive?: boolean
  customViews?: InterfaceCustomView[]
  activeCustomViewId?: string | null
  /** The page state an HTML interface opens with from the active custom view */
  customViewState?: Record<string, unknown> | null
  translations?: InterfaceTranslation
  /** Discriminator added in HTML Interfaces feature. Older payloads omit it. */
  interface_type?: 'drag_drop' | 'html'
  /** HTML payload — present only when interface_type === 'html'.
   *  This is the version the iframe renders: external CDN references have
   *  been rewritten by the API to go through the same-origin proxy. */
  html?: string
  /** Original, unrewritten HTML — same content the user/LLM authored, with
   *  CDN URLs intact. Used by the in-UI dual-editor so the user sees the
   *  human-readable form. Falls back to `html` for older API responses. */
  html_source?: string
  /** Python source defining the HTMLInterface class — present only when
   *  interface_type === 'html'. Used by the in-UI editor. */
  python_code?: string
  /** Short-lived JWT signed by the API; iframe sends it as Bearer. */
  session_token?: string
}

export type GridLayout = {
  components: IMinBaseComponent[]
  properties: GridLayoutProperties
}

export interface PyplanLayout {
  [key: string]: GridLayout
}

export interface FilteredComponentValue {
  values: string[]
  componentId: string
  interfaceId: string
  indexes?: number[]
}

export interface InterfaceEvent {
  event_type: InterfaceEventType
  params: any
  componentName?: string
}

export type InterfaceCode = {
  python_code: string
}

export type ComponentCode = {
  python_code: string
}

export interface ComponentProps<T = IBaseComponent> {
  component: T
  eventCallback: (triggerData: InterfaceEvent) => void
  editMode: boolean
  context?: Context
  interfaceId?: string
  widgetResultDimensions?: { width: number; height: number }
  onContentLoaded?: () => void
  showPivotToolbar?: boolean
  updateShowPivotToolbar?: () => void
  updateShowQuickPivot?: () => void
  showQuickPivot?: boolean
}

export interface MenuComponentProps extends ComponentProps<IMenu> {
  handleCloseMenuFromInterface?: () => void
  small?: boolean
  compact?: boolean
  searchText?: string
  menuFromTab?: boolean
}

export interface ToolbarProps<T = IBaseComponent> {
  component: T
  eventCallback: (triggerData: InterfaceEvent) => void
  actionCallback?: actionCallback
  context?: Context
  interfaceId?: string
}

export interface SelectorComponentProps extends ComponentProps<ISelectorComponent> {
  triggerCallback: (T: SetSelectorValueParams) => void
  getOptionsAndIndexMethod: GetOptionsAndIndexMethod
}

export interface IndexFilterDialogProps {
  eventCallback: (triggerData: InterfaceEvent) => void
  interfaceId?: string
  nodeId?: string
  open?: boolean
  indexComponentId?: string
}

export interface ItemHeaderProps extends ComponentProps<GenericComponent> {
  hideTitle?: boolean
  hideActions?: boolean
  context?: Context
  fromCustomCode?: boolean
  interfaceId?: string
  showId?: boolean
  showLoader?: boolean
  showToggleView?: boolean
  showResultToolbarIcon?: boolean
  showResultToolbar?: boolean
  showToggleScenarios?: boolean
  handleShowResultToolbar?: () => void
  nodeNotFound?: boolean
  initialIcon?: IconDefinition
  showPivotToolbar?: boolean
  updateShowPivotToolbar?: () => void
  updateShowQuickPivot?: () => void
  handleToggleSortMode?: () => void
  sortMode?: boolean
  showQuickPivot?: boolean
  /**
   * When true, the header chrome (title, action icons, loader) is not rendered.
   * Only the "three dots" options button is shown, floating over the top-right
   * corner of the widget and revealed on hover. Used for components whose header
   * is hidden (`hide_header`) while not in edit mode.
   */
  floatingOptionsOnly?: boolean
  /** Element the floating options button is portaled into instead of floating over the corner. */
  optionsSlotElement?: HTMLElement | null
  /**
   * Refreshes the component's own data in place. Components that fetch from the
   * REST API instead of the engine (e.g. scheduled tasks) pass it so the
   * options menu can offer a "Refresh" entry without remounting the widget.
   */
  onRefresh?: () => void
}

export interface ComponentResultWrapperProps extends ItemHeaderProps {
  children: JSX.Element
  /**
   * The widget has never rendered yet and `children` is the loading skeleton.
   * The wrapper receives a stand-in component (its `component_type` is forced to
   * `Core_Empty`), so anything that keys off the real type has to be suppressed
   * until the result arrives.
   */
  isPlaceholder?: boolean
}
export type ListenerItem = {
  item: Element
  listener: EventListener
}

export enum UndoRedoComponentCodeAction {
  UNDO = 'undo',
  REDO = 'redo'
}
